import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import { getCampaigns, getLineProtection } from '../services/apiCampaigns';
import { EstadoChip, EstadoDeLinea, pct } from '../components/campaigns/shared';

const Metrica = ({ valor, etiqueta }) => (
  <div>
    <p className="text-base font-black text-slate-900 dark:text-white tabular-nums leading-none">{valor}</p>
    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-1">{etiqueta}</p>
  </div>
);

const TarjetaCampana = ({ campana }) => {
  const e = campana.estadisticas ?? {};
  const avance = pct((e.enviados ?? 0) + (e.fallidos ?? 0) + (e.omitidos ?? 0), e.total ?? 0);

  return (
    <Link
      to={campana.estado === 'borrador' ? `/campaigns/${campana.id}/edit` : `/campaigns/${campana.id}`}
      className="block bg-white dark:bg-surface-dark rounded-2xl border border-gray-100 dark:border-gray-800 p-5 hover:shadow-md hover:border-primary/30 transition-all"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-bold text-slate-900 dark:text-white truncate">{campana.nombre}</p>
          <p className="text-[11px] text-slate-500 dark:text-gray-400 mt-0.5 flex items-center gap-1.5">
            <span className="material-symbols-outlined text-[14px]">{campana.modo === 'ia' ? 'auto_awesome' : 'description'}</span>
            {campana.modo === 'ia' ? 'Redactada por IA' : 'Plantilla'}
            {campana.recurrente && <span className="ml-1 px-1.5 py-0.5 rounded bg-primary/10 text-primary font-bold">Automática</span>}
          </p>
        </div>
        <EstadoChip estado={campana.estado} />
      </div>

      {campana.pausa_motivo && campana.estado === 'pausada' && (
        <p className="mt-3 text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10 rounded-lg px-3 py-2 line-clamp-2">
          {campana.pausa_motivo}
        </p>
      )}

      {campana.estado !== 'borrador' && (
        <>
          <div className="mt-4 h-1.5 rounded-full bg-slate-100 dark:bg-white/10 overflow-hidden">
            <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${avance}%` }} />
          </div>
          <div className="mt-4 grid grid-cols-4 gap-2">
            <Metrica valor={`${e.enviados ?? 0}/${e.total ?? 0}`} etiqueta="Enviados" />
            <Metrica valor={`${pct(e.leidos ?? 0, e.enviados ?? 0)}%`} etiqueta="Leídos" />
            <Metrica valor={e.respondieron ?? 0} etiqueta="Respondieron" />
            <Metrica valor={e.ventas ?? 0} etiqueta="Ventas" />
          </div>
        </>
      )}
    </Link>
  );
};

export default function CampaignsPage() {
  const navigate = useNavigate();

  const { data: campanas = [], isLoading } = useQuery({
    queryKey: ['campaigns'],
    queryFn: async () => (await getCampaigns()).data,
    refetchInterval: 30000,
  });

  const { data: proteccion } = useQuery({
    queryKey: ['campaigns-protection'],
    queryFn: async () => (await getLineProtection()).data,
    refetchInterval: 30000,
  });

  const activas = campanas.filter(c => ['enviando', 'programada', 'pausada'].includes(c.estado));
  const resto = campanas.filter(c => !['enviando', 'programada', 'pausada'].includes(c.estado));

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-background-dark">
      <Header title="Campañas" />

      <div className="flex-1 overflow-y-auto p-4 md:p-8 no-scrollbar">
        <div className="max-w-6xl mx-auto space-y-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-text-main dark:text-white">Campañas de WhatsApp</h2>
              <p className="text-sm text-text-secondary max-w-2xl">
                Reactiva leads fríos y consultas sin cerrar con mensajes personalizados. Salen de a uno, en horario y sin pasarse de lo que la línea aguanta.
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                onClick={() => navigate('/campaigns/protection')}
                className="px-4 py-2 rounded-xl font-bold text-sm flex items-center gap-2 border border-gray-200 dark:border-gray-700 text-slate-700 dark:text-gray-200 hover:bg-white dark:hover:bg-white/5 transition-colors"
              >
                <span className="material-symbols-outlined text-[20px]">shield</span>
                Protección de la línea
              </button>
              <button
                onClick={() => navigate('/campaigns/new')}
                className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-xl font-bold text-sm flex items-center gap-2 shadow-sm transition-all"
              >
                <span className="material-symbols-outlined text-[20px]">add</span>
                Nueva campaña
              </button>
            </div>
          </div>

          {proteccion?.lineas?.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {proteccion.lineas.map(l => <EstadoDeLinea key={l.linea_id} linea={l} />)}
            </div>
          )}

          {isLoading ? (
            <p className="py-12 text-center text-text-secondary">Cargando campañas…</p>
          ) : campanas.length === 0 ? (
            <div className="py-16 text-center bg-white dark:bg-surface-dark rounded-3xl border border-dashed border-gray-200 dark:border-gray-800">
              <span className="material-symbols-outlined text-5xl text-primary/30">campaign</span>
              <p className="mt-3 font-bold text-slate-800 dark:text-white">Todavía no hay campañas</p>
              <p className="text-sm text-text-secondary mt-1 max-w-md mx-auto">
                Empieza por una pequeña: por ejemplo, los cotizados de la última semana que no respondieron.
              </p>
            </div>
          ) : (
            <>
              {activas.length > 0 && (
                <section className="space-y-3">
                  <h3 className="text-xs font-black text-slate-500 dark:text-gray-400 uppercase tracking-widest">En curso</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {activas.map(c => <TarjetaCampana key={c.id} campana={c} />)}
                  </div>
                </section>
              )}
              {resto.length > 0 && (
                <section className="space-y-3">
                  <h3 className="text-xs font-black text-slate-500 dark:text-gray-400 uppercase tracking-widest">Borradores e historial</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {resto.map(c => <TarjetaCampana key={c.id} campana={c} />)}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
